import { useState, useCallback } from "react";
import { exportToCSV } from "../../utils/dashboardSharedUtils";
import { authFetch, authHeaders } from "../../utils/authFetch";

const API_ROOT_BASE = (() => {
  const envBase = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  if (envBase) {
    const base = envBase.endsWith("/api") ? envBase.slice(0, -4) : envBase;
    return `${base.replace(/\/+$/, "")}/api`;
  }
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:8000/api";
    }
    return `${window.location.origin}/api`;
  }
  return "https://app.neurolonic.com/api";
})();

const normalizeId = (value) =>
  value === undefined || value === null || value === "" ? null : String(value);

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
};

export default function useStaffImportExport({ staff, userDetails, t, onImportComplete }) {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const exportStaffToCSV = useCallback(() => {
    if (!staff.length) {
      alert(t("No staff to export"));
      return;
    }

    const headers = [
      "Name",
      "Role",
      "Contact",
      "Last Session",
      "Calm",
      "Stressed",
      "Demotivated",
      "Anxious",
      "Hurt",
      "Burnout Score",
    ];

    const rows = staff.map((s) => [
      s.name || "",
      s.role || "",
      s.contact || "",
      s.last_session || "",
      s.eve?.calm ?? 0,
      s.eve?.stressed ?? 0,
      s.eve?.demotivated ?? 0,
      s.eve?.anxious ?? 0,
      s.eve?.hurt ?? 0,
      s.burnout_score ?? 0,
    ]);

    exportToCSV(headers, rows, "staff_export");
  }, [staff, t]);

  const handleImportStaffCSV = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.name.endsWith(".csv")) {
        alert(t("Please select a CSV file"));
        return;
      }
      setImporting(true);
      setImportResult(null);
      try {
        const userId =
          userDetails?.user_id || localStorage.getItem("neuro_user_id");
        const formData = new FormData();
        formData.append("file", file);
        const res = await authFetch(
          `${API_ROOT_BASE}/school/import-staff?university_id=${encodeURIComponent(userId || "")}`,
          {
            method: "POST",
            headers: authHeaders(),
            body: formData,
          },
        ).then((r) => r.json());
        if (res.status_code === 200) {
          setImportResult({
            success: true,
            count: res.data?.length || 0,
            message: res.message,
          });
          if (onImportComplete) onImportComplete();
        } else {
          setImportResult({
            success: false,
            message: res.message || "Import failed",
          });
        }
      } catch (err) {
        setImportResult({ success: false, message: "Failed to import" });
      }
      setImporting(false);
      e.target.value = null;
    },
    [userDetails, t, onImportComplete],
  );

  return {
    importing,
    importResult,
    exportStaffToCSV,
    handleImportStaffCSV,
  };
}
